<script setup lang="ts">
    import { computed, watch } from 'vue'
    import { useElementBounding, useWindowSize } from '@vueuse/core'
    import { chatCoversPageControlBar } from './utils'

    /**
     * The agent acting on the page that embeds the widget, and the user's stop
     * (spec `page-tools.md`, "Presa di controllo"). Fixed on the page, where
     * the user looks while the agent acts. The widget renders it next to the
     * chat, not inside it: a transformed ancestor would pin a fixed element to
     * itself instead of to the page. It comes down into place and goes back
     * up, so its own transition lives here: a `v-if` on the component would
     * remove it before it could leave.
     *
     * A chat in front of the bar's place (a panel that fills a phone) covers
     * the page as well: the user reads the steps and stops them in the chat,
     * and the bar would only hide the chat's header. There it does not show.
     */
    const props = defineProps<{
        /** The agent is acting on the page: the bar shows. */
        active: boolean
        /** The step running, as its tool names it. */
        action?: string
        /** The chat that shows the bar, measured against the bar's place. */
        chat?: Element | null
    }>()

    const emit = defineEmits<{
        stop: []
    }>()

    const { left, right, top, bottom, update } = useElementBounding(
        () => (props.chat ?? undefined) as HTMLElement | undefined,
    )
    const { width } = useWindowSize()
    const chatCoversBar = computed(
        () =>
            !!props.chat &&
            chatCoversPageControlBar(
                {
                    left: left.value,
                    right: right.value,
                    top: top.value,
                    bottom: bottom.value,
                },
                width.value,
            ),
    )
    // A panel that slid in moved with a transform, which no observer
    // reports: measured again when the agent starts acting
    watch(
        () => props.active,
        (active) => {
            if (active) {
                update()
            }
        },
    )
</script>

<template>
    <Transition name="pk-page-control-bar" appear>
        <div
            v-if="active && !chatCoversBar"
            class="pk-page-control-bar"
            role="status">
            <span class="pk-page-control-bar__text">
                {{ $t('messagePart.pageControlActive') }}
                <template v-if="action">· {{ action }}</template>
            </span>
            <button
                type="button"
                class="pk-page-control-bar__stop"
                @click="emit('stop')">
                {{ $t('action.stop') }}
            </button>
        </div>
    </Transition>
</template>

<style lang="scss">
    .pk-page-control-bar {
        position: fixed;

        // Below the beam that runs along the top edge of the frame while the
        // agent works (12px from the edge, glowing 32px inwards): higher, the
        // bar hid the one sign that the agent is at work
        top: var(--spacing-48);
        left: 50%;
        transform: translateX(-50%);

        // Above the site's own layers, whatever they are: the bar has to stay
        // in view on a page the widget does not control.
        z-index: 2147483647;
        display: flex;
        align-items: center;
        gap: var(--spacing-12);

        // Room for the sentence and the stop, the step after them trails off:
        // a navigation's whole path stretched it across the page
        max-width: min(var(--spacing-384), calc(100vw - var(--spacing-32)));
        padding: var(--spacing-8) var(--spacing-8) var(--spacing-8)
            var(--spacing-16);
        border-radius: var(--rounded-full);

        // The theme's text and background, swapped: a dark bar on a light
        // theme and the other way round, readable on any site.
        background-color: var(--color-word-1);
        color: var(--color-surface);
        font-family: var(--font-sans);
        font-size: var(--text-14);
        font-weight: var(--font-medium);
        box-shadow: var(--shadow-lg);

        &__text {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__stop {
            flex-shrink: 0;
            border: 0;
            border-radius: var(--rounded-full);
            padding: var(--spacing-6) var(--spacing-14);
            background-color: var(--color-surface);
            color: var(--color-word-1);
            font: inherit;
            cursor: pointer;

            &:focus-visible {
                outline: var(--spacing-2) solid var(--color-surface);
                outline-offset: var(--spacing-2);
            }
        }
    }

    // In from above with the curve of the copilot's own opening, out the way
    // it came, faster. `translate` and `scale` leave alone the `transform`
    // that centers the bar
    .pk-page-control-bar-enter-active {
        transition:
            opacity 0.25s ease-out,
            translate 0.35s cubic-bezier(0.32, 0.72, 0, 1),
            scale 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    }

    .pk-page-control-bar-leave-active {
        transition:
            opacity 0.2s ease-in,
            translate 0.2s ease-in,
            scale 0.2s ease-in;
    }

    .pk-page-control-bar-enter-from,
    .pk-page-control-bar-leave-to {
        opacity: 0;
        translate: 0 calc(-1 * var(--spacing-16));
        scale: 0.96;
    }

    @media (prefers-reduced-motion: reduce) {
        .pk-page-control-bar-enter-active,
        .pk-page-control-bar-leave-active {
            transition: none;
        }
    }
</style>
